import type { ReactNode } from 'react';
import type { ProjectCalibrationHealthSummary } from '../api/types';
import type { LibraryDensity } from '../hooks/useDisplayPreferences';
import { ago, percentDone, stateLabel } from './libraryFamilies';
import { ROW_COLUMNS, type RowColumn } from './projectRowColumns';
import './toolbar/toolbar.css';

/** The pills the Library rows and the plan list share, so a project reads
 *  the same way wherever it is listed: which database, what state Target
 *  Scheduler has it in, how far along, how it graded, and when. */

export function DbPill({ name, title }: { name: string; title?: string }) {
  // The inner span carries the ellipsis, which a flex pill cannot.
  return <span className="library-pill library-pill-db" title={title}><span className="library-pill-text">{name}</span></span>;
}

/** Target Scheduler's project state: Draft, Active, Inactive or Closed. */
export function StatePill({ state }: { state: number | null | undefined }) {
  if (state === null || state === undefined) return null;
  return <span className={`library-pill library-pill-state is-state-${state}`}>{stateLabel(state)}</span>;
}

/** The state as a select, when the user may write the database: a choice
 *  asks first, then writes the project row in Target Scheduler, which the
 *  rig's scheduler acts on. */
export function StateControl({ state, name, dbName, editable, pending = null, onChange }: { state: number | null | undefined; name: string; dbName: string; editable: boolean; pending?: number | null; onChange: (state: number) => void }) {
  if (!editable || state === null || state === undefined) return <StatePill state={state} />;
  // While a write is in flight the select shows the choice and takes no
  // other: arrow keys on a closed select fire a change per step.
  const shown = pending ?? state;
  const choose = (next: number) => {
    // The select is controlled, so a refusal leaves it on the saved state.
    if (pending !== null || next === state) return;
    if (window.confirm(`Set ${name} to ${stateLabel(next)} in ${dbName}? Target Scheduler uses this state the next time it selects a target.`)) onChange(next);
  };
  // The pill draws the caret, inside its rounded edge.
  return <span className={`library-pill library-pill-state library-pill-select is-state-${shown}`}>
    <select value={shown} aria-label={`State of ${name} in ${dbName}`} disabled={pending !== null}
      title="Target Scheduler project state in this database" onChange={event => choose(Number(event.target.value))}>
      {[0, 1, 2, 3].map(value => <option key={value} value={value}>{stateLabel(value)}</option>)}
    </select>
  </span>;
}

/** Accepted frames against the goal, with a small bar; "Done" once the goal
 *  is met; the image count when there is no goal. */
export function ProgressPill({ accepted, desired, totalImages, title }: { accepted: number; desired: number; totalImages: number; title?: string }) {
  const done = percentDone(accepted, desired);
  if (desired <= 0) return <span className="library-pill library-pill-progress is-open" title={title ?? 'No desired frame count in Target Scheduler'}>{totalImages} images</span>;
  const finished = accepted >= desired;
  return <span className={`library-pill library-pill-progress${finished ? ' is-done' : ''}`} title={title ?? `${accepted} of ${desired} desired frames accepted`}>
    <span className="library-pill-bar" aria-hidden="true"><span style={{ width: `${Math.min(100, done ?? 0)}%` }} /></span>
    {accepted} / {desired} · {finished ? 'Done' : `${done}%`}
  </span>;
}

export function GradingPill({ accepted, rejected, pending }: { accepted: number; rejected: number; pending: number }) {
  return <span className="library-pill library-pill-grading" title={`${accepted} accepted, ${rejected} rejected, ${pending} pending`}>
    <span className="grade-accepted">{accepted}</span><span className="grade-rejected">{rejected}</span><span className="grade-pending">{pending}</span>
  </span>;
}

// Two-digit month and day, so every range in the list is the same width.
const formatDay = (seconds: number) => new Date(seconds * 1000).toLocaleDateString(undefined, { year: 'numeric', month: '2-digit', day: '2-digit' });

/** First to last capture, with how long ago the last one was. A narrow
 *  Library hides the range and keeps the age. */
export function DatesPill({ earliest, latest, nowMs, title }: { earliest: number | null | undefined; latest: number | null | undefined; nowMs: number; title?: string }) {
  const last = ago(latest, nowMs);
  return <span className="library-pill library-pill-dates" title={title}>
    <span className="library-dates-range">{latest ? `${formatDay(earliest ?? latest)} – ${formatDay(latest)}` : 'No dates'}</span>
    {last ? <small className="library-dates-ago">{last}</small> : null}
  </span>;
}

const lightCount = (count: number) => `${count} light${count === 1 ? '' : 's'}`;

/** How well the calibration library serves the project's lights: Poor when
 *  some cannot be calibrated properly, Check when something is worth a look.
 *  Nothing until the background check has a current answer. Opens the
 *  project's calibration report. */
export function CalibrationPill({ health, name, onOpen }: { health: ProjectCalibrationHealthSummary | undefined; name: string; onOpen: () => void }) {
  if (!health?.level) return null;
  const poor = health.level === 'poor';
  const word = poor ? 'Poor' : 'Check';
  const title = poor
    ? `${lightCount(health.poor_lights)} can't be calibrated properly${health.check_lights > 0 ? `; ${lightCount(health.check_lights)} to check` : ''}`
    : `${lightCount(health.check_lights)} to check`;
  return <button type="button" className={`library-pill library-pill-calibration is-${health.level}`} title={`Calibration: ${title}`}
    aria-label={`Calibration ${word} for ${name}`} onClick={onOpen}>
    <span aria-hidden="true">🧪</span>{word}
  </button>;
}

/** The outer pill's own marker: one plan shot by several rigs. */
export function FamilyPill({ rigs }: { rigs: number }) {
  return <span className="library-pill library-pill-family" title="One plan shot by several rigs: the same Target Scheduler project in each database">{rigs} rigs</span>;
}

/** The arrow that opens a row into its card and folds it back. */
export function FoldButton({ open, name, onClick }: { open: boolean; name: string; onClick: () => void }) {
  return <button type="button" className={`library-expand${open ? ' is-open' : ''}`} aria-expanded={open}
    aria-label={`${open ? 'Hide' : 'Show'} details for ${name}`} title={open ? 'Collapse details' : 'Show details'} onClick={onClick}>{open ? '▾' : '▸'}</button>;
}

/** The Compact / Detailed switch both lists carry, as the toolbar's
 *  segmented group: one radio of two is on. */
export function DensityToggle({ density, onChange }: { density: LibraryDensity; onChange: (next: LibraryDensity) => void }) {
  return <div className="tool-segmented project-density" role="radiogroup" aria-label="Project view">
    <button type="button" role="radio" aria-checked={density === 'compact'} onClick={() => onChange('compact')}>Compact</button>
    <button type="button" role="radio" aria-checked={density === 'detailed'} onClick={() => onChange('detailed')}>Detailed</button>
  </div>;
}

/** One compact row: the fold arrow, the name, then pills. */
export function ProjectRow({ className, testId, children }: { className?: string; testId?: string; children: ReactNode }) {
  return <div className={['library-row', className].filter(Boolean).join(' ')} data-testid={testId ?? 'library-row'}>{children}</div>;
}

/** A Library row's cells, one per column in column order. A row without a
 *  pill still gets the empty cell, so the pills after it keep their x. */
export function RowCells({ cells }: { cells: Partial<Record<RowColumn, ReactNode>> }) {
  return <>{ROW_COLUMNS.map(column => <div key={column} className={`library-cell library-cell-${column}`} data-column={column}>{cells[column]}</div>)}</>;
}
